import {Component, computed, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {PlanningItemHelper} from '@application/helper/planning-prototype/planning-item-helper';
import {FixedSchedule} from '@domain/planning-prototype/carpet/fixed-schedule';
import {FixedSchedulePlaceholder} from '@domain/planning-prototype/carpet/fixed-schedule-placeholder';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {FixedScheduleToPlan} from '@domain/planning-prototype/to-plan/fixed-schedule-to-plan';
import {FixedScheduleOverviewService} from '@presentation/pages/textile-data/fixed-schedule/fixed-schedule-overview.service';
import {GridOptionsBuilderFactoryService, SelectGridDialog} from '@vdw/angular-component-library';
import {GetRowIdParams, GridApi, GridOptions, GridReadyEvent} from 'ag-grid-community';
import {PlanningPrototypeContextService} from '../../../utilities/planning-prototype-context.service';
import {SelectFixedScheduleParams} from './select-fixed-schedule-params';

@Component({
  selector: 'app-select-fixed-schedule',
  templateUrl: './select-fixed-schedule.component.html',
  providers: [FixedScheduleOverviewService]
})
export class SelectFixedScheduleComponent implements SelectGridDialog {
  private readonly gridOptionsBuilder = inject(GridOptionsBuilderFactoryService);
  private readonly planningContext = inject(PlanningPrototypeContextService);
  private readonly dialogRef = inject(MatDialogRef<SelectFixedScheduleComponent>);
  private readonly dialogParams = inject<SelectFixedScheduleParams>(MAT_DIALOG_DATA);
  private readonly overviewService = inject(FixedScheduleOverviewService);

  public listOfGridOptions = [this.initGridOptions()];
  public listOfFixedSchedules = computed(() => this.filterFixedSchedules());
  public listOfGridApis: GridApi<FixedSchedule>[] = [];

  public confirmSelection(): void {
    const selectedRows = this.listOfGridApis[0].getSelectedRows();
    this.dialogRef.close(selectedRows);
  }

  private filterFixedSchedules(): FixedSchedule[] {
    const fixedSchedulesToExclude = this.dialogParams.itemsSelectedAlready
      .filter((item: PlanningItem): item is FixedSchedulePlaceholder => item instanceof FixedSchedulePlaceholder)
      .map((placeholder: FixedSchedulePlaceholder) => placeholder.fixedSchedule.id);
    return this.planningContext.fixedSchedulesToPlan
      .filter(
        (toPlan: FixedScheduleToPlan) =>
          PlanningItemHelper.isFixedScheduleCompatible(toPlan, this.dialogParams.machine, this.dialogParams.creel, this.dialogParams.machineQuality) &&
          !fixedSchedulesToExclude.includes(toPlan.fixedSchedule.id)
      )
      .map((toPlan: FixedScheduleToPlan) => toPlan.fixedSchedule);
  }

  private initGridOptions(): GridOptions<FixedSchedule> {
    return this.gridOptionsBuilder
      .getBuilder(this.overviewService.getColumnDefs(false), GridIdentifier.SELECT_FIXED_SCHEDULE)
      .withOnGridReady((ready: GridReadyEvent<FixedSchedule>) => this.listOfGridApis.push(ready.api))
      .withColumnMenu()
      .withGetRowId((params: GetRowIdParams<FixedSchedule>) => `${params.data.id}`)
      .withRowSelection(true)
      .build();
  }
}
